/* eslint-disable max-lines */
import { CbhIcon, Title } from "@clipboard-health/ui-components";
import { isDefined } from "@clipboard-health/util-ts";
import { Box, DialogContent, Fade, Stack, TextField } from "@mui/material";
import { DialogFooter } from "@src/appV2/redesign/components/DialogFooter";
import { LoadingButton } from "@src/appV2/redesign/components/LoadingButton";
import { SelectableBox } from "@src/appV2/redesign/components/SelectableBox";
import { TextButton } from "@src/appV2/redesign/components/TextButton";
import { useFormContext } from "react-hook-form";

import { ReviewQuestionType } from "../../redesign/Workplace/Review/types";
import {
  type NumberQuestionConfig,
  type PostShiftReviewFormValues,
  QUESTION_TAG_ICON_MAP,
  QuestionTag,
  type StepAction,
} from "../types";

type NumberValidationResult = { isError: true; errorMessage: string } | { isError: false };

function validateNumberInput(params: {
  value: string | undefined;
  minValue: number;
  maxValue: number;
}): NumberValidationResult {
  const { value, minValue, maxValue } = params;

  if (!isDefined(value) || value.length === 0) {
    return { isError: true, errorMessage: "Value must not be empty." };
  }

  const numberValue = Number(value);

  if (!Number.isSafeInteger(numberValue)) {
    return { isError: true, errorMessage: "Please enter a valid number" };
  }

  if (numberValue < minValue || numberValue > maxValue) {
    return {
      isError: true,
      errorMessage: `Please enter a number between ${minValue} and ${maxValue}.`,
    };
  }

  return { isError: false };
}

export interface QuestionStepProps {
  questionTag: QuestionTag.SOFTWARE | QuestionTag.SUPPLIES | QuestionTag.PATIENTS_PER_NURSE;
  questionText: string;
  questionType: ReviewQuestionType;
  options?: Array<{ id: string; displayText: string }>;
  numberConfig?: NumberQuestionConfig;
  isSubmitting?: boolean;
  onAction: (action: StepAction) => void;
}

export function QuestionStep({
  questionTag,
  questionText,
  questionType,
  options,
  numberConfig,
  isSubmitting = false,
  onAction,
}: QuestionStepProps) {
  const { watch, setValue } = useFormContext<PostShiftReviewFormValues>();

  const isNumberType = questionType === ReviewQuestionType.NUMBER;
  const isMultipleChoiceType = questionType === ReviewQuestionType.MULTIPLE_CHOICE;

  // Get the appropriate form field values based on question tag
  function getFormFieldValues() {
    switch (questionTag) {
      case QuestionTag.SOFTWARE: {
        return {
          answer: watch("softwareAnswer"),
          otherText: watch("softwareOtherText") ?? "",
        };
      }

      case QuestionTag.SUPPLIES: {
        return {
          answer: watch("suppliesAnswer"),
          otherText: "",
        };
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        return {
          answer: watch("patientCountAnswer"),
          otherText: "",
        };
      }

      default: {
        return { answer: undefined, otherText: "" };
      }
    }
  }

  const { answer: selectedAnswer, otherText } = getFormFieldValues();
  const showOtherInput = selectedAnswer === "Other";
  const hasAnswer = isDefined(selectedAnswer) && selectedAnswer !== "";

  const iconMap = QUESTION_TAG_ICON_MAP;

  // Use provided options from API, fallback to empty array if not provided
  const displayOptions = options?.map((opt) => opt.displayText) ?? [];

  // For NUMBER type, numberConfig.minValue and numberConfig.maxValue are guaranteed to exist
  const numberValidation =
    isNumberType && hasAnswer && numberConfig !== undefined
      ? validateNumberInput({
          value: selectedAnswer,
          minValue: numberConfig.minValue,
          maxValue: numberConfig.maxValue,
        })
      : { isError: false as const };

  const setFormFieldValue = (field: "answer" | "otherText", value: string | undefined) => {
    switch (questionTag) {
      case QuestionTag.SOFTWARE: {
        if (field === "answer") {
          setValue("softwareAnswer", value, { shouldValidate: true });
        } else {
          setValue("softwareOtherText", value ?? "", { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.SUPPLIES: {
        if (field === "answer") {
          setValue("suppliesAnswer", value, { shouldValidate: true });
        }

        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        if (field === "answer") {
          setValue("patientCountAnswer", value, { shouldValidate: true });
        }

        break;
      }

      default: {
        // No form field updates for unrecognized question tags
        break;
      }
    }
  };

  function clearFormFields() {
    switch (questionTag) {
      case QuestionTag.SOFTWARE: {
        setValue("softwareAnswer", undefined, { shouldValidate: true });
        setValue("softwareOtherText", "", { shouldValidate: true });
        break;
      }

      case QuestionTag.SUPPLIES: {
        setValue("suppliesAnswer", undefined, { shouldValidate: true });
        break;
      }

      case QuestionTag.PATIENTS_PER_NURSE: {
        setValue("patientCountAnswer", undefined, { shouldValidate: true });
        break;
      }

      default: {
        // No form field clearing for unrecognized question tags
        break;
      }
    }
  }

  const handleOptionSelect = (option: string) => {
    if (selectedAnswer === option) {
      // Unselect by clicking again
      clearFormFields();
    } else {
      setFormFieldValue("answer", option);
      if (option !== "Other") {
        setFormFieldValue("otherText", "");
      }
    }
  };

  // Derive continue button disabled state based on question type
  const isContinueDisabled = isNumberType
    ? numberValidation.isError
    : selectedAnswer === "Other" && otherText.trim().length === 0;

  return (
    <>
      <DialogContent sx={{ py: 2 }}>
        <Stack spacing={6} sx={{ px: 0 }}>
          <Stack alignItems="center" spacing={4}>
            <CbhIcon type={iconMap[questionTag]} size="large" />
            <Title variant="h3" component="h3" textAlign="center">
              {questionText}
            </Title>
          </Stack>

          {/* NUMBER type: show numeric input */}
          {isNumberType && (
            <Stack spacing={3} sx={{ pt: 5 }}>
              <TextField
                placeholder="e.g. 8"
                value={selectedAnswer ?? ""}
                disabled={isSubmitting}
                error={numberValidation.isError}
                helperText={numberValidation.isError ? numberValidation.errorMessage : undefined}
                type="number"
                inputProps={{
                  pattern: "[0-9]*",
                  inputMode: "numeric",
                }}
                onChange={(event) => {
                  // Only allow numeric input
                  const value = event.target.value.replace(/[^0-9]/g, "");
                  setFormFieldValue("answer", value);
                }}
              />
            </Stack>
          )}

          {/* MULTIPLE_CHOICE type: show selectable options */}
          {isMultipleChoiceType && (
            <>
              <Stack spacing={3} sx={{ pt: 5 }}>
                {displayOptions.map((option) => (
                  <SelectableBox
                    key={option}
                    variant="primary"
                    startIconType="building"
                    endIconType={selectedAnswer === option ? "checkmark" : undefined}
                    isSelected={selectedAnswer === option}
                    disabled={isSubmitting}
                    sx={{
                      "& > div": {
                        textAlign: "left",
                        ml: 3,
                      },
                    }}
                    onToggleSelect={() => {
                      handleOptionSelect(option);
                    }}
                  >
                    {option}
                  </SelectableBox>
                ))}
              </Stack>

              {showOtherInput && (
                <TextField
                  placeholder="Documentation software name"
                  value={otherText}
                  disabled={isSubmitting}
                  onChange={(event) => {
                    setFormFieldValue("otherText", event.target.value);
                  }}
                />
              )}
            </>
          )}
        </Stack>
      </DialogContent>

      <DialogFooter orientation="vertical">
        <Stack spacing={3} sx={{ width: "100%", height: "100px" }}>
          {hasAnswer && (
            <Fade in appear>
              <Box>
                <LoadingButton
                  fullWidth
                  variant="contained"
                  size="large"
                  isLoading={isSubmitting}
                  disabled={isContinueDisabled}
                  onClick={() => {
                    onAction({
                      type: "NEXT_STEP",
                      data: {
                        answer: selectedAnswer!,
                        otherText: selectedAnswer === "Other" ? otherText : undefined,
                      },
                    });
                  }}
                >
                  Continue
                </LoadingButton>
              </Box>
            </Fade>
          )}
          {!hasAnswer && !isSubmitting && (
            <Fade in appear>
              <Box sx={{ alignSelf: "center" }}>
                <TextButton
                  onClick={() => {
                    onAction({ type: "SKIP_STEP" });
                  }}
                >
                  Skip
                </TextButton>
              </Box>
            </Fade>
          )}
        </Stack>
      </DialogFooter>
    </>
  );
}
/* eslint-enable max-lines */
